<script setup>
// FLOW-06 (SPEC S4; PLAN G15, G24; Tasks 6.1 e 6.6) — a página da seção Execuções da aba PowerFlow:
// busca a página de execuções da conta (`GET /powerflow/execucoes`) e a entrega à lista aprovada no
// mockup (`ExecucoesDaConta`), que só mostra e diz o que mudou nos filtros.
//
// **Filtros e páginas.** O primeiro pedido sai com o período que a barra mostra (7 dias), e cada
// mudança da barra é um pedido novo, da primeira página. "Carregar mais antigas" pede a seguinte pelo
// cursor (`antes`, o `proximo` da anterior) e a junta embaixo. As opções do filtro de flow são os nomes
// da lista de Flows da conta (G24: o filtro vale para todas as versões). A resposta de um pedido velho
// não pisa na de um novo. Se a primeira lista não vem, a página diz e oferece tentar de novo; se um
// filtro novo falha, a barra volta aos filtros da lista que está na tela, e um alerta diz por quê.
//
// **Ao vivo** (S4, "a tela atualiza ao vivo enquanto a execução roda"; `execucoesAoVivo.js`): a página
// assina o canal da conta. A mudança de estado muda a linha no lugar, e a execução nova entra no topo
// quando casa com os filtros; o que chega enquanto a lista carrega é aplicado depois dela. A linha nova
// de um agente chega só com o `agent_ref` (o Rails completa o nome na lista e no detalhe, não no
// canal): o detalhe dela é relido, num lote só por meio segundo, e o nome entra. A cada (re)conexão, a
// primeira página é relida e mesclada, para o que passou com o cabo fora. Sem cabo, ela é relida a
// cada 10 s.
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import powerflowExecucoesApi from 'dashboard/api/powerflowExecucoes';
import powerflowFlowsApi from 'dashboard/api/powerflowFlows';
import ExecucoesDaConta from './ExecucoesDaConta.vue';
import PainelDeFalha from './PainelDeFalha.vue';
import { filtrosIniciais, pedidoDosFiltros } from './filtrosDasExecucoes';
import {
  aplicarNaLista,
  assinarExecucoes,
  juntarExecucao,
  mesclarPagina,
} from './execucoesAoVivo';

// O lote que traz várias linhas novas de agente vira uma releitura só.
const ESPERA_DO_NOME = 500;
const SONDAGEM_SEM_CABO = 10000;

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const accountId = computed(() => Number(route.params.accountId));

const filtros = ref(filtrosIniciais());
// Os filtros e o pedido da lista que está na tela.
let aplicados = {
  filtros: filtros.value,
  pedido: pedidoDosFiltros(filtros.value),
};
const lista = ref({ execucoes: [], proximo: null });
const nomesDosFlows = ref([]);
const carregando = ref(true);
const falhou = ref(false);
let jaCarregou = false;
let carregandoMais = false;
// Cada pedido da primeira página tem a vez dele: a resposta de um velho não pisa na do novo.
let vez = 0;
// Os eventos que chegam enquanto a lista carrega, para aplicar por cima dela.
let pendentes = null;

const mensagemDoServidor = erro =>
  erro?.response?.data?.erro?.mensagem ||
  erro?.response?.data?.mensagem ||
  null;

function aplicar(eventos) {
  const resultado = aplicarNaLista(
    lista.value.execucoes,
    eventos,
    aplicados.pedido
  );
  lista.value = { ...lista.value, execucoes: resultado.execucoes };
  return resultado.aCompletar;
}

// -- o nome do agente nas linhas novas
const aCompletar = new Set();
let esperaDoNome = null;

async function completarNomes() {
  esperaDoNome = null;
  const ids = [...aCompletar];
  aCompletar.clear();
  await Promise.all(
    ids.map(async id => {
      try {
        const { quem } = await powerflowExecucoesApi.show(accountId.value, id);
        lista.value = {
          ...lista.value,
          execucoes: lista.value.execucoes.map(item =>
            item.execucao_id === id ? juntarExecucao(item, { quem }) : item
          ),
        };
      } catch {
        // Fica o `agent_ref` ("Agente sayuri") até a próxima leitura da lista.
      }
    })
  );
}

function pedirNomes(ids) {
  if (!ids.length) return;
  ids.forEach(id => aCompletar.add(id));
  if (!esperaDoNome) esperaDoNome = setTimeout(completarNomes, ESPERA_DO_NOME);
}

// -- a lista
async function carregar(pedido = aplicados.pedido, comFiltros = filtros.value) {
  vez += 1;
  const minha = vez;
  carregando.value = true;
  falhou.value = false;
  pendentes = [];
  try {
    const pagina = await powerflowExecucoesApi.index(accountId.value, pedido);
    if (minha !== vez) return;
    aplicados = { filtros: comFiltros, pedido };
    jaCarregou = true;
    lista.value = pagina;
    if (pendentes.length) pedirNomes(aplicar(pendentes));
  } catch (erro) {
    if (minha !== vez) return;
    if (jaCarregou) {
      filtros.value = aplicados.filtros;
      useAlert(
        mensagemDoServidor(erro) ||
          t('POWER_TOOLS.POWERFLOW.EXECUTIONS.FILTER_ERROR')
      );
    } else {
      falhou.value = true;
    }
  } finally {
    if (minha === vez) {
      carregando.value = false;
      pendentes = null;
    }
  }
}

const filtrar = pedido => carregar(pedido, filtros.value);

async function carregarMaisAntigas() {
  if (carregandoMais || carregando.value || !lista.value.proximo) return;
  carregandoMais = true;
  const minha = vez;
  try {
    const pagina = await powerflowExecucoesApi.index(accountId.value, {
      ...aplicados.pedido,
      antes: lista.value.proximo,
    });
    if (minha !== vez) return;
    const ja = new Set(lista.value.execucoes.map(item => item.execucao_id));
    lista.value = {
      execucoes: [
        ...lista.value.execucoes,
        ...pagina.execucoes.filter(item => !ja.has(item.execucao_id)),
      ],
      proximo: pagina.proximo,
    };
  } catch {
    useAlert(t('POWER_TOOLS.POWERFLOW.EXECUTIONS.LOAD_MORE_ERROR'));
  } finally {
    carregandoMais = false;
  }
}

function abrir(execucao) {
  router.push({
    name: 'powerflow_execucao',
    params: { accountId: accountId.value, execucaoId: execucao.execucao_id },
  });
}

// O filtro de flow pelo nome (G24): sem a lista de Flows, ele fica sem opções, e o resto segue.
async function carregarNomesDosFlows() {
  try {
    const { flows } = await powerflowFlowsApi.index(accountId.value);
    nomesDosFlows.value = flows.map(flow => flow.nome);
  } catch {
    nomesDosFlows.value = [];
  }
}

// -- ao vivo
let assinatura = null;
let sondagem = null;

function receber(eventos) {
  if (pendentes) {
    pendentes.push(...eventos);
    return;
  }
  if (!jaCarregou) return;
  pedirNomes(aplicar(eventos));
}

// A primeira página de novo, por cima da lista (o que passou com o cabo fora).
async function mesclarPrimeiraPagina() {
  if (carregando.value || !jaCarregou) return;
  const minha = vez;
  try {
    const pagina = await powerflowExecucoesApi.index(
      accountId.value,
      aplicados.pedido
    );
    if (minha !== vez || carregando.value) return;
    lista.value = {
      execucoes: mesclarPagina(lista.value.execucoes, pagina.execucoes),
      proximo: lista.value.execucoes.length
        ? lista.value.proximo
        : pagina.proximo,
    };
  } catch {
    // A próxima (re)conexão ou sondagem tenta de novo.
  }
}

function semCabo() {
  if (!sondagem)
    sondagem = setInterval(mesclarPrimeiraPagina, SONDAGEM_SEM_CABO);
}

onMounted(() => {
  carregar();
  carregarNomesDosFlows();
  assinatura = assinarExecucoes(accountId.value, {
    aoReceber: receber,
    aoConectar: mesclarPrimeiraPagina,
    aoRecusar: () => {
      assinatura = null;
      semCabo();
    },
  });
  if (!assinatura) semCabo();
});

onBeforeUnmount(() => {
  assinatura?.unsubscribe();
  clearInterval(sondagem);
  clearTimeout(esperaDoNome);
});
</script>

<template>
  <div class="flex-1 min-w-0 px-6 pt-4 pb-8 overflow-y-auto">
    <section v-if="falhou" class="flex flex-col max-w-6xl gap-4">
      <header class="flex flex-col gap-1 max-w-3xl">
        <h2 class="mb-0 text-heading-1 text-n-slate-12">
          {{ t('POWER_TOOLS.POWERFLOW.EXECUTIONS.TITLE') }}
        </h2>
        <p class="mb-0 text-sm text-n-slate-11">
          {{ t('POWER_TOOLS.POWERFLOW.EXECUTIONS.DESCRIPTION') }}
        </p>
      </header>
      <PainelDeFalha
        :mensagem="t('POWER_TOOLS.POWERFLOW.EXECUTIONS.LOAD_ERROR')"
        @tentar="carregar()"
      />
    </section>
    <ExecucoesDaConta
      v-else
      v-model:filtros="filtros"
      class="max-w-6xl"
      :lista="lista"
      :flows="nomesDosFlows"
      :carregando="carregando"
      @filtrar="filtrar"
      @mais-antigas="carregarMaisAntigas"
      @abrir="abrir"
    />
  </div>
</template>
